import MediaRecorderBase from "../../core/mediarecorder/mediarecorder.base";
import {
  BaseTexture,
  Color4,
  Constants,
  CopyTextureToTexture,
  Effect,
  Engine,
  PostProcess,
  RenderTargetTexture,
  Scene,
  Vector2,
  WebXRDefaultExperience,
  WebXRRawCameraAccess,
} from "babylonjs";
import { MediaRecorderResponse } from "../../core/mediarecorder/mediarecorder.types";
import { CanvasRecorder } from "./mediarecorder.web.recorder";
import WebGLBlitter from "./mediarecorder.web.gl";

export default class MediaRecorderWeb extends MediaRecorderBase {
  private width: number = 0;
  private height: number = 0;
  private recordingFrameRate: number = 25;
  private debugCanvas: boolean = false;

  // Babylon
  private scene?: Scene;
  private engine?: Engine;
  private xr?: WebXRDefaultExperience;
  private recorder?: CanvasRecorder;

  // Render Targets
  private sceneRenderTarget?: RenderTargetTexture;
  private cameraRenderTarget?: RenderTargetTexture;
  private babylonTextureCopier?: CopyTextureToTexture;

  // Post Processing
  private postProcessor?: PostProcess;
  private cameraAccess?: WebXRRawCameraAccess;
  private renderFrame: () => void = this.renderPixels.bind(this);

  // WebGL Blitter
  private glBlitter: WebGLBlitter = new WebGLBlitter();

  // Debug
  private divFPS: HTMLDivElement = document.getElementById(
    "fps"
  ) as HTMLDivElement;

  // Capture every Nth frame
  private frameCounter: number = 0;
  private recording: boolean = false;

  constructor() {
    super();
    this.setRecordingResolution();
  }

  private setRecordingResolution = () => {
    const canvasSize: Vector2 = new Vector2(screen.availWidth, screen.availHeight);

    let canvasWidth = Math.ceil(canvasSize.x * window.devicePixelRatio);
    let canvasHeight = Math.ceil(canvasSize.y * window.devicePixelRatio);

    const res: '480p' | '720p' | '1080p' = '720p';
    const maxSize: { w: number, h: number } = { w: 480, h: 640 };
    if (res === '720p') {
      maxSize.w = 720;
      maxSize.h = 1280;
    } else if (res === '1080p') {
      maxSize.w = 1080;
      maxSize.h = 1920;
    }
    const scaleFactor = Math.min(maxSize.w / canvasWidth, maxSize.h / canvasHeight);
    if (scaleFactor < 1) {
      canvasWidth = Math.floor(canvasWidth * scaleFactor);
      canvasHeight = Math.floor(canvasHeight * scaleFactor);
    }

    // Ensure even dimensions for encoding
    this.width = canvasWidth & ~1;
    this.height = canvasHeight & ~1;
  }

  //---------------------------------------------------------------- init.
  public init(scene: Scene, xr: WebXRDefaultExperience) {
    this.initShaders();

    this.scene = scene;
    this.engine = this.scene.getEngine() as Engine;
    this.xr = xr;

    this.recordingFrameRate = 25;
    this.debugCanvas = false;


    this.engine!.setSize(this.width, this.height, true);
    this.engine!.setViewport(this.scene!.activeCamera!.viewport, this.width, this.height);

    this.engine!.setSize(this.width, this.height, true);
    this.engine!.setViewport(this.scene!.activeCamera!.viewport, this.width, this.height);

    if (this.xr) {
      const baseExperience = this.xr.baseExperience;
      const featuresManager = baseExperience.featuresManager;
      this.cameraAccess = featuresManager.enableFeature(
        WebXRRawCameraAccess,
        "latest"
      ) as WebXRRawCameraAccess;
    }
  }

  //---------------------------------------------------------------- start.
  public async start(): Promise<MediaRecorderResponse> {
    if (this.recording) {
      return {
        success: false,
        error: "mediarecorder.web.start.error: start called when already recording.",
        url: undefined,
      };
    }
    this.initRecording();
    if (!this.engine!._renderingCanvas) {
      return {
        success: false,
        error: "mediarecorder.web.start.error: canvas not found.",
        url: undefined,
      };
    }
    try {
      await this.recorder!.startRecording(this.recordingFrameRate);
      this.recording = true;
    }
    catch (e) {
      console.error("mediarecorder.web.start.error:", e);
      return {
        success: false,
        error: "mediarecorder.web.start.error: " + e,
        url: undefined,
      };
    }
    return {
      success: true,
      error: undefined,
      url: undefined,
    };
  }

  //---------------------------------------------------------------- stop.
  public async stop(): Promise<MediaRecorderResponse> {
    if (!this.recording) {
      return {
        error: "mediarecorder.web.stop.error: stop called without first recording.",
        success: true,
        url: undefined,
      }
    }
    this.killRecording();
    this.recording = false;
    const response = await this.recorder!.stopRecording();
    return response;
  }

  //---------------------------------------------------------------- private.
  private initShaders() {
    Effect.ShadersStore["mediarecorderCompFragmentShader"] = `
      precision highp float;
      varying vec2 vUV;
      uniform sampler2D textureSampler;
      uniform sampler2D cameraSampler;
  
      vec4 alphaBlend(vec4 src, vec4 dst) {
        float final_alpha = src.a + dst.a * (1.0 - src.a);
        float inv_final_alpha = 1.0 / max(final_alpha, 0.00001); // Avoid division by zero using max()
        return vec4((src.rgb * src.a + dst.rgb * dst.a * (1.0 - src.a)) * inv_final_alpha, final_alpha);
      }
  
      void main(void) {
        vec4 colorCamera = texture2D(cameraSampler, vUV);
        vec4 colorRender = texture2D(textureSampler, vUV);
        vec4 colorFinal = alphaBlend(colorRender, colorCamera);
        gl_FragColor = colorFinal;
      }
    `;
  }

  private initRecording() {
    // BabylonJS RenderTargetTexture - To store Scene Pixels

    this.engine!.setSize(this.width, this.height, true);
    this.engine!.setViewport(this.scene!.activeCamera!.viewport, this.width, this.height);
    this.recorder = new CanvasRecorder(this.engine!.getRenderingCanvas()!, this.width, this.height);
    this.glBlitter.init(this.engine!._gl);

    this.sceneRenderTarget = new RenderTargetTexture(
      "depth",
      {
        width: this.width,
        height: this.height,
      },
      this.scene,
      true
    );

    this.sceneRenderTarget.clearColor = new Color4(0, 0, 0, 0);
    this.sceneRenderTarget.renderList = null; // Render everything
    this.scene!.customRenderTargets.push(this.sceneRenderTarget);

    // Stores the XR Camera Pixels
    this.cameraRenderTarget = new RenderTargetTexture(
      "camera",
      {
        width: this.width,
        height: this.height,
      },
      null,
      true
    );

    this.postProcessor = new PostProcess(
      "mediarecorderComp", // name
      "mediarecorderComp", // fragment shader
      null, // uniforms
      ["textureSampler", "cameraSampler"], // samplers
      1.0, // ratio
      null, // camera (null as this is output to offscreen canvas)
      Constants.TEXTURE_NEAREST_SAMPLINGMODE,
      this.engine, // engine
      false // reusable
    );

    this.postProcessor.onApply = (effect) => {
      if (this.cameraRenderTarget)
        effect.setTexture("cameraSampler", this.cameraRenderTarget);
    };

    this.sceneRenderTarget.addPostProcess(this.postProcessor);
    this.scene!.onAfterRenderCameraObservable.add(this.renderFrame);

    this.babylonTextureCopier = new CopyTextureToTexture(this.engine!, false);

    // Handle XR Setup
    if (this.xr) {
      this.cameraAccess?.onTexturesUpdatedObservable.add(
        this.onXRCameraTextureUpdated.bind(this)
      );
    } else {
      console.error("mediarecorder.web.initRecording: xr not found.");
    }
  }

  private killRecording() {

    this.glBlitter.cleanup();

    if (this.postProcessor)
      this.sceneRenderTarget?.removePostProcess(this.postProcessor);

    // Remove the custom render targets
    this.scene!.customRenderTargets = this.scene!.customRenderTargets.filter(
      (rt) => rt !== this.sceneRenderTarget
    );

    // this.engine.onEndFrameObservable.removeCallback(this.boundRenderFrameA);
    this.scene!.onAfterRenderCameraObservable.removeCallback(
      this.renderFrame
    );

    this.sceneRenderTarget?.dispose();
    this.cameraRenderTarget?.dispose();

    this.postProcessor?.dispose();
    this.babylonTextureCopier?.dispose();

    this.cameraAccess?.onTexturesUpdatedObservable.clear();
  }

  // Callback from WebXR when the camera texture is updated
  private onXRCameraTextureUpdated(textures: BaseTexture[]) {
    if (this.babylonTextureCopier && this.cameraRenderTarget) {
      const cameraTexture = textures[0];
      this.babylonTextureCopier.copy(cameraTexture, this.cameraRenderTarget);
    }
  }

  // Render out the pixels to the offscreen canvas
  private renderPixels() {
    this.frameCounter++;
    if (this.divFPS)
      this.divFPS.innerText = this.engine!.getFps().toFixed(2) + " fps";

    this.glBlitter.blitRTTToCanvas(
      this.sceneRenderTarget!,
      this.width,
      this.height
    );
    this.recorder!.capture();
    this.recorder!.encode();
  }
}
